<template>
  <div class="form-group searchable-select"
       :class="form_group_class">
    <div class="d-flex justify-content-between items-center">
      <label v-if="label"
             :for="id">
        {{ label }}
      </label>
      <span class="text-danger"
            v-if="warning">{{ warning }}</span>
    </div>

    <div class="search-select-container">
      <input ref="input"
             :class="'form-control ' + (size ? 'form-control-' + size : '')"
             :id="id"
             type="text"
             v-model="searchTerm"
             :placeholder="selectedOption && selectedOption.name ? selectedOption.name : 'พิมพ์เพื่อค้นหา...'"
             @focus="showDropdown = true; isUserTyping = false"
             @input="onInput"
             @keydown="handleKeydown"
             autocomplete="off" />
      <div class="dropdown-icon"
           @click="toggleDropdown">
        <i class="fas fa-chevron-down"
           :class="{ 'rotated': showDropdown }"></i>
      </div>
      <div v-if="showDropdown"
           class="dropdown-menu"
           ref="dropdown">
        <div v-for="(option, i) in filteredOptions"
             :key="i"
             class="dropdown-item"
             :class="{ 'active': i === highlightedIndex, 'selected': value && option && value === option.value }"
             @click="selectOption(option)"
             @mouseenter="highlightedIndex = i">
          {{ option && option.name ? option.name : 'Invalid option' }}
        </div>
        <div v-if="!filteredOptions || filteredOptions.length === 0"
             class="dropdown-item disabled">
          ไม่พบตัวเลือก
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SearchSelect",
  props: {
    id: {
      type: String,
      required: true,
    },

    options: {
      type: Array,
      default: () => [],
    },

    label: {
      type: String,
    },

    value: {
      type: null,
    },

    form_group_class: {
      type: String,
      default: "",
    },

    size: {
      type: String,
      default: "",
    },

    warning: {
      type: String,
      default: null,
    }
  },

  data() {
    return {
      searchTerm: "",
      showDropdown: false,
      highlightedIndex: -1,
      isUserTyping: false, // Track if user is actively typing
    };
  },

  computed: {
    selectedOption() {
      if (!this.options || !Array.isArray(this.options) || !this.value) {
        return null;
      }
      return this.options.find(option => option && option.value === this.value);
    },

    filteredOptions() {
      if (!this.options || !Array.isArray(this.options)) {
        return [];
      }
      // If user is not actively typing, show all options
      if (!this.isUserTyping || !this.searchTerm) {
        return this.options;
      }
      // Filter only when user is typing
      return this.options.filter(option =>
        option && option.name &&
        option.name.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    },
  },

  watch: {
    showDropdown(show) {
      if (show) {
        this.isUserTyping = false; // Show all options when dropdown opens
        this.$nextTick(() => {
          document.addEventListener('click', this.handleClickOutside);
        });
      } else {
        document.removeEventListener('click', this.handleClickOutside);
        this.highlightedIndex = -1;
        this.isUserTyping = false; // Reset when closing
      }
    },
  },

  created() { },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    initializeComponent() {
      // Initialize search term based on current value, similar to Select component
      if (this.value && this.options && Array.isArray(this.options)) {
        const selectedOption = this.options.find(option => option && option.value === this.value);
        if (selectedOption && selectedOption.name) {
          this.searchTerm = selectedOption.name;
        }
      }
    },
    onInput() {
      this.showDropdown = true;
      this.highlightedIndex = -1;
      this.isUserTyping = true; // User is actively typing
      // Clear selection if user is typing something different
      if (this.selectedOption && this.selectedOption.name && this.searchTerm !== this.selectedOption.name) {
        this.$emit("input", null);
      }
    },

    selectOption(option) {
      if (!option) return;
      this.isUserTyping = false; // Reset typing flag
      this.searchTerm = option.name || '';
      this.showDropdown = false;
      this.$emit("input", option.value);
    },

    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) {
        this.isUserTyping = false; // Show all options when toggling open
        if (this.$refs.input) {
          this.$nextTick(() => {
            this.$refs.input.focus();
          });
        }
      }
    },

    handleKeydown(event) {
      if (!this.showDropdown) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
          this.showDropdown = true;
          return;
        }
      }

      const filteredOptionsLength = this.filteredOptions ? this.filteredOptions.length : 0;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.highlightedIndex = Math.min(
            this.highlightedIndex + 1,
            filteredOptionsLength - 1
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
          break;
        case 'Enter':
          event.preventDefault();
          if (this.highlightedIndex >= 0 && this.filteredOptions && this.filteredOptions[this.highlightedIndex]) {
            this.selectOption(this.filteredOptions[this.highlightedIndex]);
          }
          break;
        case 'Escape':
          this.showDropdown = false;
          if (this.$refs.input) {
            this.$refs.input.blur();
          }
          break;
      }
    },

    handleClickOutside(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.showDropdown = false;
        this.isUserTyping = false; // Reset typing flag
        // Reset search term to selected option if user clicked outside
        this.initializeComponent();
      }
    },
  },

  mounted() {
    this.initializeComponent();
    this.$emit("input", this.selectedOption ? this.selectedOption.value : null);
  },
};
</script>

<style scoped lang="scss">
.searchable-select {
  position: relative;
}

.search-select-container {
  position: relative;

  .dropdown-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: #6c757d;
    transition: transform 0.3s ease;

    &:hover {
      color: #495057;
    }

    i.rotated {
      transform: rotate(180deg);
    }
  }

  input.form-control {
    padding-right: 35px;
    cursor: pointer;
  }
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ced4da;
  border-top: none;
  border-radius: 0 0 0.25rem 0.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 9999;
  display: block;

  .dropdown-item {
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 1px solid #f8f9fa;
    transition: background-color 0.2s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:last-child {
      border-bottom: none;
    }

    &:hover,
    &.active {
      background-color: #f8f9fa;
      color: #495057;
    }

    &.selected {
      background-color: #007bff;
      color: white;

      &:hover,
      &.active {
        background-color: #0056b3;
      }
    }

    &.disabled {
      color: #6c757d;
      cursor: not-allowed;
      background-color: transparent;

      &:hover {
        background-color: transparent;
      }
    }
  }
}

// Custom scrollbar for dropdown
.dropdown-menu::-webkit-scrollbar {
  width: 6px;
}

.dropdown-menu::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.dropdown-menu::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>
